import { CommonModule } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import type { IColumnFilter, IColumnFilterValue, ISorter, ISorterValue } from '@coreui/angular-pro';
import {
  ButtonDirective,
  ButtonCloseDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  DateRangePickerModule,
  FormModule,
  FormSelectDirective,
  ModalModule,
  MultiSelectComponent,
  MultiSelectOptionComponent,
  RowComponent,
  SmartTableComponent,
  SmartTableModule,
  TemplateIdDirective
} from '@coreui/angular-pro';
import { cilBan, cilPencil, cilUserFollow } from '@coreui/icons';
import { IconDirective, IconSetService } from '@coreui/icons-angular';
import { AuthService } from 'auth';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';
import { isSuperAdmin as isSuperAdminUser, FoundingMemberBadgeComponent, Logger, User as SharedUser } from 'shared';
import { environment } from '../../../environments/environment';

/** The community sanction in force (admin users index, `community_sanction`). */
interface CommunitySanctionSummary {
  kind: 'mute' | 'ban' | 'hide';
  scope: 'network' | 'group' | 'channel';
  scope_name: string | null;
  until: string | null;
}

interface User {
  id: number;
  community_sanction?: CommunitySanctionSummary | null;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  sponsor_id: number | null;
  level: number;
  direct_cards: number;
  created_at: string;
  date_of_joining: string | null;
  email_confirmed: boolean;
  email_confirmed_at: string | null;
  balance: number; // USDT (legacy)
  balance_usd?: number; // total valued in USD across all currencies
  balances?: { currency: string; balance: number; balance_usd: number }[];
  admin_fee_paid_until: string | null;
  address: string | null;
  address2: string | null;
  city: string | null;
  postcode: string | null;
  state_code: string | null;
  country_code: string | null;
  mobile: string | null;
  dob: string | null;
  gender: string | null;
  language: string | null;
  user_type: string | null;
  xshop: boolean;
  xshop_admin: boolean;
  /** Founding Member — one of the first 1.000 paying Health Club members. */
  founding_member?: boolean;
  founding_member_number?: number | null;
  founding_member_since?: string | null;
  /** Founding Practitioner — the same for the first 1.000 Practitioner Network members. */
  founding_practitioner?: boolean;
  founding_practitioner_number?: number | null;
  founding_practitioner_since?: string | null;
  deleted: boolean;
  deleted_at: string | null;
  sponsored_active_count: number;
  sponsored_inactive_count: number;
  sponsored_unconfirmed_count: number;
  sponsored_deleted_count: number;
  registration_config_domain_id: number | null;
  registration_domain_name: string | null;
  domain_visits?: { config_domain_id: number; name: string; first_seen_at: string; last_seen_at: string; visit_count: number }[];
  order?: { total_price: number };
}

interface UsersResponse {
  users: User[];
  total_count: number;
  page: number;
  per_page: number;
  total_pages: number;
}

interface Country {
  code: string;
  name: string;
  states?: { code: string; name: string }[];
}

interface SelectOption {
  value: string;
  label: string;
}

interface EditUserForm {
  email: string;
  first_name: string;
  last_name: string;
  sponsor_id: number | null;
  level: number;
  direct_cards: number;
  balance: number;
  date_of_joining: string;
  email_confirmed: boolean;
  admin_fee_paid_until: string;
  address: string;
  address2: string;
  city: string;
  postcode: string;
  state_code: string;
  country_code: string;
  mobile: string;
  dob: string;
  gender: string;
  language: string;
  user_type: string;
  xshop: boolean;
  xshop_admin: boolean;
}

interface ApiToken {
  id: number;
  user_id: number;
  user_email: string;
  created_by_id: number;
  created_by_email: string;
  description: string | null;
  expires_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  active: boolean;
  created_at: string;
}

interface GenerateTokenResponse {
  token: string;
  token_id: number;
  user_id: number;
  user_email: string;
  expires_at: string;
  message: string;
}

interface GenerateTokenForm {
  user_id: number | null;
  description: string
}

@Component({
  selector: 'app-users-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ContainerComponent,
    RowComponent,
    ColComponent,
    ButtonDirective,
    ButtonCloseDirective,
    FormSelectDirective,
    DateRangePickerModule,
    SmartTableModule,
    SmartTableComponent,
    ModalModule,
    FormModule,
    TemplateIdDirective,
    MultiSelectComponent,
    MultiSelectOptionComponent,
    IconDirective,
    TranslatePipe,
    FoundingMemberBadgeComponent
  ],
  templateUrl: './users-page.component.html',
  styleUrls: ['./users-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UsersPageComponent {
  private http = inject(HttpClient);
  private logger = new Logger('UsersPageComponent');
  private iconSet = inject(IconSetService);
  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private i18n = inject(I18nService);
  private destroyRef = inject(DestroyRef);

  // ────────────────────────────────────────────────
  // State signals
  // ────────────────────────────────────────────────
  readonly allowBalanceEdit = environment.allowBalanceEdit;

  currentUser = signal<SharedUser | null>(null);
  isSuperAdmin = signal(false);
  /** Community sanctions are only offered to an admin who has the community (`flag:community_enabled`). */
  communityEnabled = computed(() => this.currentUser()?.community_enabled === true);

  users = signal<any[]>([]);
  loading = signal(false);
  totalCount = signal(0);
  totalPages = signal(0);

  currentPage = signal(1);
  itemsPerPage = signal(20);

  sortColumn = signal<string>('id');
  sortDirection = signal<'asc' | 'desc'>('asc');

  columnFilters = signal<IColumnFilterValue>({});

  userStatusFilter = signal<string>('all');

  // 'all' | 'yes' | 'no' — Founding Members (first 1.000 paying Health Club
  // members). The building block for founder-only mailings and event invites.
  foundingMemberFilter = signal<string>('all');

  createdAtStartDate = signal<Date | null>(null);
  createdAtEndDate = signal<Date | null>(null);
  dateOfJoiningStartDate = signal<Date | null>(null);
  dateOfJoiningEndDate = signal<Date | null>(null);

  // Edit modal
  editModalVisible = signal(false);
  editingUser = signal<User | null>(null);
  editForm = signal<EditUserForm>({
    email: '',
    first_name: '',
    last_name: '',
    sponsor_id: null,
    level: 0,
    direct_cards: 0,
    balance: 0,
    date_of_joining: '',
    email_confirmed: false,
    admin_fee_paid_until: '',
    address: '',
    address2: '',
    city: '',
    postcode: '',
    state_code: '',
    country_code: '',
    mobile: '',
    dob: '',
    gender: '',
    language: '',
    user_type: '',
    xshop: false,
    xshop_admin: false
  });

  // Countries
  countries = signal<SelectOption[]>([]);
  selectedCountryStates = computed<SelectOption[]>(() => {
    const code = this.editForm().country_code;
    const country = this.countriesData.find(c => c.code === code);
    return country?.states?.map(s => ({ value: s.code, label: s.name })) || [];
  });
  // Payout API Token state (super admin only)
  apiTokens = signal<ApiToken[]>([]);
  generateTokenModalVisible = signal(false);
  generatedToken = signal<string | null>(null);
  generatedTokenExpires = signal<string>('');
  tokenCopied = signal(false);
  generateTokenForm = signal<GenerateTokenForm>({
    user_id: null as number | null,
    description: ''
  });

  // Soft delete confirmation modal state
  deleteConfirmModalVisible = signal(false);
  userToDelete = signal<any | null>(null);

  countriesData: Country[] = [];

  // ────────────────────────────────────────────────
  // Computed / config
  // ────────────────────────────────────────────────
  dateRanges = computed(() => ({
    'Last 7 days': [this.getDaysAgo(7), new Date()],
    'Last 30 days': [this.getDaysAgo(30), new Date()],
    'Last 90 days': [this.getDaysAgo(90), new Date()]
  }));

  columns = computed<any[]>(() => [
    { key: 'actions', label: '', sorter: false, filter: false},
    { key: 'id', label: this.translate.translate('admin.users.columnId'), sorter: true, filter: true },
    { key: 'email', label: this.translate.translate('admin.users.columnEmail'), sorter: true, filter: true },
    { key: 'first_name', label: this.translate.translate('admin.users.columnFirstName'), sorter: true, filter: true },
    { key: 'last_name', label: this.translate.translate('admin.users.columnLastName'), sorter: true, filter: true },
    { key: 'sponsor_id', label: this.translate.translate('admin.users.columnSponsorId'), sorter: true, filter: true },
    { key: 'level', label: this.translate.translate('admin.users.columnLevel'), sorter: true, filter: true },
    { key: 'direct_cards', label: this.translate.translate('admin.users.columnDirectCards'), sorter: true, filter: true },
    { key: 'balance', label: this.translate.translate('admin.users.columnBalance'), sorter: true, filter: false },
    { key: 'user_status', label: this.translate.translate('admin.users.columnStatus'), sorter: false, filter: false },
    // Founding Member number (1..1000). Sortable so the founding members can be
    // listed in the order they joined; filtered through the dropdown above.
    { key: 'founding_member_number', label: this.translate.translate('founding_member.title'), sorter: true, filter: false },
    { key: 'sponsorship_stats', label: this.translate.translate('admin.users.columnSponsorshipStats'), sorter: false, filter: false },
    { key: 'created_at', label: this.translate.translate('admin.users.columnCreatedAt'), sorter: true, filter: false },
    { key: 'date_of_joining', label: this.translate.translate('admin.users.columnDateOfJoining'), sorter: true, filter: false },
    { key: 'email_confirmed', label: this.translate.translate('admin.users.columnEmailConfirmed'), sorter: true, filter: false },
    { key: 'domains', label: this.translate.translate('admin.users.columnDomains'), sorter: false, filter: false }
  ]);

  columnFilterConfig: IColumnFilter = { external: true };
  columnSorterConfig: ISorter = { external: true };
  itemsPerPageSelectConfig = { external: true };

  // ────────────────────────────────────────────────
  // Effects
  // ────────────────────────────────────────────────
  constructor() {
    this.iconSet.icons = { ...this.iconSet.icons, cilBan, cilPencil, cilUserFollow };

    // Current user / super admin check
    effect(() => {
      this.authService.getUser$()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(user => {
        this.currentUser.set(user);
        this.isSuperAdmin.set(isSuperAdminUser(user));
      });
    });

    // Reload on any filter/sort/page/date change
    effect(() => {
      this.currentPage();
      this.itemsPerPage();
      this.sortColumn();
      this.sortDirection();
      this.columnFilters();
      this.userStatusFilter();
      this.foundingMemberFilter();
      this.createdAtStartDate();
      this.createdAtEndDate();
      this.dateOfJoiningStartDate();
      this.dateOfJoiningEndDate();
      this.loadUsers();
    });

    this.loadCountries();
    this.loadApiTokens();
  }

  // ────────────────────────────────────────────────
  // Core actions
  // ────────────────────────────────────────────────
  private loadUsers(): void {
    this.loading.set(true);

    let params = new HttpParams()
      .set('page', this.currentPage().toString())
      .set('per_page', this.itemsPerPage().toString())
      .set('sort_column', this.sortColumn())
      .set('sort_direction', this.sortDirection());

    Object.entries(this.columnFilters()).forEach(([key, val]) => {
      if (val) params = params.set(`filter_${key}`, val.toString());
    });

    if (this.userStatusFilter() !== 'all') {
      params = params.set('filter_user_status', this.userStatusFilter());
    }

    if (this.foundingMemberFilter() !== 'all') {
      params = params.set('filter_founding_member', this.foundingMemberFilter());
    }

    if (this.createdAtStartDate()) params = params.set('filter_created_at_start', this.formatDateForAPI(this.createdAtStartDate()!));
    if (this.createdAtEndDate()) params = params.set('filter_created_at_end', this.formatDateForAPI(this.createdAtEndDate()!));
    if (this.dateOfJoiningStartDate()) params = params.set('filter_date_of_joining_start', this.formatDateForAPI(this.dateOfJoiningStartDate()!));
    if (this.dateOfJoiningEndDate()) params = params.set('filter_date_of_joining_end', this.formatDateForAPI(this.dateOfJoiningEndDate()!));

    this.http.get<UsersResponse>('/admin/users', { params })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            const transformed = response.users.map(user => {
              let userStatus = '';
              let statusClass = '';
              let rowClass = '';

              if (user.deleted) {
                userStatus = this.translate.translate('admin.users.statusDeleted');
                statusClass = 'text-danger fw-bold bg-danger bg-opacity-10';
                rowClass = 'bg-danger bg-opacity-10 text-muted';
              } else if (!user.email_confirmed_at) {
                userStatus = this.translate.translate('admin.users.statusUnconfirmed');
                statusClass = 'text-danger fw-bold';
              } else if (user.date_of_joining) {
                userStatus = this.translate.translate('admin.users.statusActive');
                statusClass = 'text-success fw-bold';
              } else {
                userStatus = this.translate.translate('admin.users.statusInactive');
                statusClass = 'text-warning fw-bold';
              }

              let sponsorshipStats = `${user.sponsored_active_count} / ${user.sponsored_inactive_count} / ${user.sponsored_unconfirmed_count}`;
              if (user.sponsored_deleted_count > 0) {
                sponsorshipStats += ` / ${user.sponsored_deleted_count}`;
              }

              return {
                _rawId: user.id,  // Store raw ID for click handler
                _rawEmail: user.email,  // Store raw email for delete modal
                _isDeleted: user.deleted,  // Store deleted status for button visibility
                _sponsoredActiveCount: user.sponsored_active_count,
                _sponsoredInactiveCount: user.sponsored_inactive_count,
                _sponsoredUnconfirmedCount: user.sponsored_unconfirmed_count,
                // Balance shown as the total valued in USD; expandable to per-currency.
                _balanceUsd: user.balance_usd ?? user.balance,
                _balances: user.balances ?? [{ currency: 'USDT', balance: user.balance, balance_usd: user.balance }],
                // Registration domain (label) + full history of domains used (expandable).
                _registrationDomain: user.registration_domain_name,
                _domainVisits: user.domain_visits ?? [],
                ...user,
                user_status: userStatus,
                sponsorship_stats: sponsorshipStats,
                created_at: this.formatDate(user.created_at),
                date_of_joining: this.formatDate(user.date_of_joining),
                deleted_at: user.deleted ? this.formatDate(user.deleted_at) : null,
                email_confirmed: user.email_confirmed
                  ? this.translate.translate('admin.users.statusYes')
                  : this.translate.translate('admin.users.statusNo'),
                _props: rowClass ? { class: rowClass } : undefined,
                _cellProps: {
                  user_status: { class: statusClass },
                  sponsorship_stats: { class: 'fw-bold' },
                  email_confirmed: {
                    class: user.email_confirmed ? 'text-success fw-bold' : 'text-warning fw-bold'
                  }
                }
              };
            });

            this.users.set(transformed);
            this.totalCount.set(response.total_count);
            this.totalPages.set(response.total_pages);
            this.loading.set(false);
          },
          error: err => {
            this.logger.error('Failed to load users', err);
            this.loading.set(false);
          }
        });
  }

  private loadCountries(): void {
    this.http.get<Country[]>('/countries')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: data => {
            this.countriesData = data;
            this.countries.set(data.map(c => ({ value: c.code, label: c.name })));
          },
          error: err => this.logger.error('Failed to load countries', err)
        });
  }

  // ────────────────────────────────────────────────
  // Event handlers
  // ────────────────────────────────────────────────
  onSorterChange(event: ISorterValue): void {
    if (event.column) {
      this.sortColumn.set(event.column as string);
      this.sortDirection.set(event.state === 'asc' ? 'asc' : 'desc');
      this.currentPage.set(1);
    }
  }

  onColumnFilterChange(event: IColumnFilterValue): void {
    this.columnFilters.set(event);
    this.currentPage.set(1);
  }

  onItemsPerPageChange(event: number): void {
    this.itemsPerPage.set(event);
    this.currentPage.set(1);
  }

  onActivePageChange(page: number): void {
    this.currentPage.set(page);
  }

  onUserStatusFilterChange(value: string): void {
    this.userStatusFilter.set(value);
    this.currentPage.set(1);
  }

  onFoundingMemberFilterChange(value: string): void {
    this.foundingMemberFilter.set(value);
    this.currentPage.set(1);
  }

  onDateRangeChange(field: string, value: any): void {
    const start = value?.startDate ?? null;
    const end = value?.endDate ?? null;

    if (field === 'created_at') {
      this.createdAtStartDate.set(start);
      this.createdAtEndDate.set(end);
    } else if (field === 'date_of_joining') {
      this.dateOfJoiningStartDate.set(start);
      this.dateOfJoiningEndDate.set(end);
    }

    this.currentPage.set(1);
  }

  // ────────────────────────────────────────────────
  // Edit Modal
  // ────────────────────────────────────────────────
  openEditModal(userId: number): void {
    this.http.get<UsersResponse>('/admin/users', {
      params: new HttpParams().set('filter_id', userId.toString())
    }) 
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: response => {
        if (response.users.length > 0) {
          const user = response.users[0];
          this.editingUser.set(user);
          this.editForm.set({
            email: user.email,
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            sponsor_id: user.sponsor_id,
            level: user.level || 0,
            direct_cards: user.direct_cards || 0,
            balance: user.balance,
            date_of_joining: user.date_of_joining || '',
            email_confirmed: user.email_confirmed,
            admin_fee_paid_until: user.admin_fee_paid_until || '',
            address: user.address || '',
            address2: user.address2 || '',
            city: user.city || '',
            postcode: user.postcode || '',
            state_code: user.state_code || '',
            country_code: user.country_code || '',
            mobile: user.mobile || '',
            dob: user.dob || '',
            gender: user.gender || '',
            language: user.language || '',
            user_type: user.user_type || '',
            xshop: user.xshop ?? false,
            xshop_admin: user.xshop_admin ?? false
          });
          this.editModalVisible.set(true);
        }
      },
      error: err => this.logger.error('Failed to load user for editing', err)
    });
  }

  closeEditModal(): void {
    this.editModalVisible.set(false);
    this.editingUser.set(null);
  }

  /**
   * Changing an economically relevant field (sponsor, user type, level, direct cards,
   * admin-fee date, balance) needs a reason of at least 10 characters, logged
   * append-only (decision G of 2026-10-03). The backend says when one is needed
   * (422 reason_required + the fields); the admin is asked once and the save retried.
   */
  saveUser(reason?: string): void {
    const user = this.editingUser();
    if (!user) return;

    const body = reason ? { ...this.editForm(), reason } : this.editForm();
    this.http.put(`/admin/users/${user.id}`, body)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.closeEditModal();
            this.loadUsers();
          },
          error: err => {
            if (err?.status === 422 && err?.error?.code === 'reason_required') {
              const fields = (err.error.fields as string[] | undefined)?.join(', ') || '';
              const answer = (prompt(`A reason is required to change ${fields} (at least 10 characters, ` +
                                     'logged in the audit trail):', reason ?? '') ?? '').trim();
              if (answer) this.saveUser(answer);
              return;
            }
            this.logger.error('Failed to update user', err);
            alert('Failed to update user: ' + (err.error?.error || 'Unknown error'));
          }
        });
  }

  // ============================================================================
  // Payout API Token Management (Super Admin only)
  // ============================================================================

  loadApiTokens(): void {
    if (!this.isSuperAdmin()) return;

    this.http.get<{ tokens: ApiToken[] }>('/admin/payout-api-tokens')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.apiTokens.set(response.tokens);
            this.logger.debug('API tokens loaded:', response.tokens.length);
          },
          error: (error) => {
            this.logger.error('Failed to load API tokens', error);
          }
        });
  }

  openGenerateTokenModal(): void {
    this.generateTokenForm.set({ user_id: null, description: '' });
    this.generatedToken.set(null);
    this.generatedTokenExpires.set('');
    this.tokenCopied.set(false);
    this.generateTokenModalVisible.set(true);
  }

  updateGenerateTokenFormField<K extends keyof GenerateTokenForm>(
    key: K,
    value: GenerateTokenForm[K]
  ) {
    this.generateTokenForm.update(form => ({
      ...form,
      [key]: value
    }));
  }

  closeGenerateTokenModal(): void {
    this.generateTokenModalVisible.set(false);
    this.generatedToken.set(null);
    if (this.isSuperAdmin()) {
      this.loadApiTokens();
    }
  }

  generateToken(): void {
    const {user_id, description} = this.generateTokenForm();
    if (!this.generateTokenForm().user_id) return;

    this.http.post<GenerateTokenResponse>('/admin/payout-api-tokens', {
      user_id,
      description: description ?? undefined
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (response) => {
        this.generatedToken.set(response.token);
        this.generatedTokenExpires.set(new Date(response.expires_at).toLocaleString());
        this.logger.info('API token generated for user', response.user_id);
      },
      error: (error) => {
        this.logger.error('Failed to generate API token', error);
        alert('Failed to generate token: ' + (error.error?.error || 'Unknown error'));
      }
    });
  }

  copyToken(): void {
    const token = this.generatedToken();
    if (token) {
      navigator.clipboard.writeText(token).then(() => {
        this.tokenCopied.set(true);
        setTimeout(() => this.tokenCopied.set(false), 2000);
      });
    }
  }

  revokeToken(tokenId: number): void {
    if (!confirm('Are you sure you want to revoke this token?')) return;

    this.http.delete(`/admin/payout-api-tokens/${tokenId}`)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.logger.info('API token revoked:', tokenId);
            this.loadApiTokens();
          },
          error: (error) => {
            this.logger.error('Failed to revoke token', error);
            alert('Failed to revoke token: ' + (error.error?.error || 'Unknown error'));
          }
        });
  }

  getCurlExample(): string {
    const token = this.generatedToken() || 'YOUR_TOKEN';
    return `# 1. List requested payouts
curl -s https://SERVER/api/payout-api/requested \\
  -H "Authorization: Bearer ${token}"

# 2. Execute payouts
curl -X POST https://SERVER/api/payout-api/execute \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{"payout_code":"CODE_FROM_STEP_1","wallet_key":"PRIVATE_KEY"}'`;
  }

  formatDateTime(dateString: string | null): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  }

  /**
   * Opens the community moderation page with the sanction form pre-filled for
   * this member (write block by default). The sanction itself, its scope,
   * duration and mandatory reason are set there.
   */
  openCommunitySanction(userId: number): void {
    this.router.navigate([`/${this.i18n.getLanguage()}/community/moderation`], {
      queryParams: { tab: 'sanction', user: userId, kind: 'mute' }
    });
  }

  communitySanctionLabel(s: CommunitySanctionSummary): string {
    const kindKey = { mute: 'sanctionKindMute', ban: 'sanctionKindBan', hide: 'sanctionKindHide' }[s.kind] ?? 'sanctionKindMute';
    const kind = this.translate.translate(`admin.users.${kindKey}`);
    if (s.scope === 'network') return `${kind} · ${this.translate.translate('admin.users.sanctionScopeNetwork')}`;
    const scope = this.translate.translate(s.scope === 'group' ? 'admin.users.sanctionScopeGroup' : 'admin.users.sanctionScopeChannel');
    return `${kind} · ${scope}${s.scope_name ? `: ${s.scope_name}` : ''}`;
  }

  communitySanctionTooltip(s: CommunitySanctionSummary): string {
    if (!s.until) return this.translate.translate('admin.users.sanctionIndefinite');
    const until = new Date(s.until);
    const formatted = Number.isNaN(until.getTime()) ? s.until : until.toLocaleString(this.i18n.getLanguage());
    return `${this.translate.translate('admin.users.sanctionUntil')} ${formatted}`;
  }

  communitySanctionClass(s: CommunitySanctionSummary): string {
    return s.kind === 'ban' ? 'bg-danger' : s.kind === 'hide' ? 'bg-dark' : 'bg-warning text-dark';
  }

  /**
   * Impersonate a user (super admin only)
   */
  impersonateUser(userId: number): void {
    if (!this.isSuperAdmin()) {
      alert('Only super admin (user ID 1) can impersonate users');
      return;
    }
    if (userId === this.currentUser()?.id) {
      alert('Cannot impersonate yourself');
      return;
    }

    const confirmed = confirm(`Are you sure you want to impersonate user ID ${userId}?\n\nAll actions will be logged in the audit trail.`);
    if (!confirmed) return;

    this.authService.impersonateUser(userId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: impersonatedUser => {
            alert(`Now impersonating: ${impersonatedUser.full_name || impersonatedUser.email}\n\nYou can stop impersonation anytime from the banner at the top.`);
            this.router.navigate(['/']);
          },
          error: err => {
            this.logger.error('Failed to start impersonation', err);
            alert('Failed to start impersonation: ' + (err.error?.error || 'Unknown error'));
          }
        });
  }

  // ============================================================================
  // Soft Delete / Restore
  // ============================================================================

  /**
   * Open the soft-delete confirmation modal for a user.
   * Shows sponsor warning if the user has sponsored users.
   */
  openDeleteConfirmModal(item: any): void {
    if (item._rawId === this.currentUser()?.id) {
      alert(this.translate.translate('admin.users.cancel'));
      return;
    }

    this.userToDelete.set(item);
    this.deleteConfirmModalVisible.set(true);
  }

  closeDeleteConfirmModal(): void {
    this.deleteConfirmModalVisible.set(false);
    this.userToDelete.set(null);
  }

  get userToDeleteHasSponsors(): boolean {
    const user = this.userToDelete();
    if (!user) return false;
    return (user._sponsoredActiveCount + user._sponsoredInactiveCount + user._sponsoredUnconfirmedCount) > 0;
  }

  getConfirmDeleteMessage(): string {
    const user = this.userToDelete();
    if (!user) return '';
    return this.translate.translate('admin.users.confirmDeleteMessage')
      .replace('%{id}', user._rawId.toString())
      .replace('%{email}', user._rawEmail);
  }

  getConfirmDeleteSponsorWarning(): string {
    const user = this.userToDelete();
    if (!user) return '';
    return this.translate.translate('admin.users.confirmDeleteSponsorWarning')
      .replace('%{active}', user._sponsoredActiveCount.toString())
      .replace('%{inactive}', user._sponsoredInactiveCount.toString())
      .replace('%{unconfirmed}', user._sponsoredUnconfirmedCount.toString());
  }

  confirmSoftDelete(): void {
    const user = this.userToDelete();
    if (!user) return;

    this.logger.info(`Soft-deleting user ${user._rawId}`);

    this.http.post(`/admin/users/${user._rawId}/soft_delete`, {})
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.logger.info(`User ${user._rawId} soft-deleted successfully`);
            this.closeDeleteConfirmModal();
            this.loadUsers();
          },
          error: (error) => {
            this.logger.error('Failed to soft-delete user', error);
            alert('Failed to delete user: ' + (error.error?.error || 'Unknown error'));
          }
        });
  }

  restoreUser(userId: number): void {
    const confirmed = confirm(
      this.translate.translate('admin.users.confirmRestoreMessage')
        .replace('%{id}', userId.toString())
        .replace('%{email}', this.users().find(u => u._rawId === userId)?._rawEmail || '')
    );
    if (!confirmed) return;

    this.logger.info(`Restoring user ${userId}`);

    this.http.post(`/admin/users/${userId}/restore`, {})
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.logger.info(`User ${userId} restored successfully`);
            this.loadUsers();
          },
          error: (error) => {
            this.logger.error('Failed to restore user', error);
            alert('Failed to restore user: ' + (error.error?.error || 'Unknown error'));
          }
        });
  }

  // ────────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────────
  private getDaysAgo(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() - days);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return this.translate.translate('admin.users.statusNA');
    return new Date(dateString).toLocaleDateString();
  }

  formatDateForAPI(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount) + ' USD';
  }

  // Balance-in-USD column: expandable per-currency breakdown (keyed by user id).
  expandedBalanceIds = signal<Set<number>>(new Set<number>());

  toggleBalanceDetails(id: number): void {
    const next = new Set(this.expandedBalanceIds());
    next.has(id) ? next.delete(id) : next.add(id);
    this.expandedBalanceIds.set(next);
  }

  isBalanceExpanded(id: number): boolean {
    return this.expandedBalanceIds().has(id);
  }

  // Domains column: expandable list of all config domains the user has used.
  expandedDomainIds = signal<Set<number>>(new Set<number>());

  toggleDomainDetails(id: number): void {
    const next = new Set(this.expandedDomainIds());
    next.has(id) ? next.delete(id) : next.add(id);
    this.expandedDomainIds.set(next);
  }

  isDomainExpanded(id: number): boolean {
    return this.expandedDomainIds().has(id);
  }

  formatUsd(amount: number): string {
    return '$' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount ?? 0);
  }

  formatAmount(amount: number, currency: string): string {
    const n = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount ?? 0);
    // USDT balances are displayed as USD (1:1)
    const display = currency === 'USDT' ? 'USD' : currency;
    return display === 'USD' ? `$${n}` : `${n} ${display}`;
  }

  getPageNumbers(): number[] {
    const total = this.totalPages();
    const current = this.currentPage();
    const range = 2;
    const pages: number[] = [];
    for (let i = Math.max(1, current - range); i <= Math.min(total, current + range); i++) {
      pages.push(i);
    }
    return pages;
  }

  getTotalUsersText(): string {
    const count = this.totalCount();
    const template = this.translate.translate('admin.users.totalUsers');
    return template.replace('%{count}', count.toString());
  }

  getShowingEntriesText(): string {
    const from = (this.currentPage() - 1) * this.itemsPerPage() + 1;
    const to = Math.min(this.currentPage() * this.itemsPerPage(), this.totalCount());
    const total = this.totalCount();
    const template = this.translate.translate('admin.users.showingEntries');
    return template
      .replace('%{from}', from.toString())
      .replace('%{to}', to.toString())
      .replace('%{total}', total.toString());
  }

  getEditUserTitle(): string {
    const user = this.editingUser();
    if (!user) return '';
    const template = this.translate.translate('admin.users.editUserTitle');
    return template.replace('%{id}', user.id.toString());
  }

  updateEditForm(changes: Partial<EditUserForm>): void {
    this.editForm.update(current => ({
      ...current,
      ...changes
    }));
  }
}
